import { APP_SCOPE } from '@nx/constants';
import * as ImportShared from '@nx/import-shared';
import { createLogger } from '@nx/logger/legacy';
import type { RootState } from '@nx/state';
import { StoreKit, createDynamicBaseQuery, prepareHeaders } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  buildDynamicDataSourceParamsPayloadByDataSourceConfig,
  buildDynamicParamsPayloadByDataSourceConfig,
  getImportUrls,
  parseApiCandidateGraphResponseToImportModel,
  sortByTimeDesc,
  transformSavedDataSourceConfig,
  waitForDataSourceSchemaJobCompletion,
} from './utils';

const logger = createLogger(APP_SCOPE.import);

const DATA_SOURCES_TAG = 'DataSources' as const;
const SOURCE_SCHEMA_TAG = 'SourceSchema' as const;

export const sourceSchemaSlice = createApi({
  tagTypes: [SOURCE_SCHEMA_TAG, DATA_SOURCES_TAG],
  reducerPath: 'sourceSchema',
  baseQuery: createDynamicBaseQuery(
    (state: RootState) => getImportUrls(state).rootEndpoint,
    {
      prepareHeaders,
    },
    (): RootState => {
      const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
      return globalStore.getState();
    },
  ),
  endpoints: (builder) => ({
    fetchDataSourceSchemaWithRawDataSourceParameters: builder.query<
      ImportShared.ApiDataSourceSchema,
      { dataSource: ImportShared.DataSourceConfig }
    >({
      queryFn: async (_arg, _api, _extraOptions, fetchWithBaseQuery) => {
        const { dataSource } = _arg;
        // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#performing-multiple-requests-with-a-single-query
        // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#performing-multiple-requests-with-a-single-query
        // This is a workaround to perform multiple requests with a single query.

        // https://stackoverflow.com/a/73982276/10890476
        // The types for the redux-toolkit fetchWithBQ are defined in a strict way where the JSON data
        // that is returned from the server has type unknown (rather than defining it loosely with any).
        // And fetchWithBQ is defined as a function that returns a Promise<QueryReturnValue<T, E, R>>,
        // which is not TypeScript generic.
        // So you do need to make an as assertion or type guard.
        const spawnNewSourceSchemaJobResult = await fetchWithBaseQuery({
          url: '/v2/sources/schema',
          method: 'POST',
          body: { dataSource: buildDynamicDataSourceParamsPayloadByDataSourceConfig(dataSource) },
        });
        const waitForDataSourceSchemaJobCompletionResult = await waitForDataSourceSchemaJobCompletion(
          spawnNewSourceSchemaJobResult,
          fetchWithBaseQuery,
        );
        return waitForDataSourceSchemaJobCompletionResult;
      },
    }),
    fetchDataSourceSchemaWithDataSourceId: builder.query<ImportShared.ApiDataSourceSchema, { dataSourceId: string }>({
      queryFn: async (_arg, _api, _extraOptions, fetchWithBaseQuery) => {
        const { dataSourceId } = _arg;
        // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#performing-multiple-requests-with-a-single-query
        // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#performing-multiple-requests-with-a-single-query
        // This is a workaround to perform multiple requests with a single query.

        // https://stackoverflow.com/a/73982276/10890476
        // The types for the redux-toolkit fetchWithBQ are defined in a strict way where the JSON data
        // that is returned from the server has type unknown (rather than defining it loosely with any).
        // And fetchWithBQ is defined as a function that returns a Promise<QueryReturnValue<T, E, R>>,
        // which is not TypeScript generic.
        // So you do need to make an as assertion or type guard.
        const spawnNewSourceSchemaJobResult = await fetchWithBaseQuery({
          url: `/v2/sources/${dataSourceId}/schema`,
          method: 'GET',
        });
        const waitForDataSourceSchemaJobCompletionResult = await waitForDataSourceSchemaJobCompletion(
          spawnNewSourceSchemaJobResult,
          fetchWithBaseQuery,
        );
        return waitForDataSourceSchemaJobCompletionResult;
      },
    }),
    fetchCandidateGraph: builder.query<
      { dataModel: ImportShared.DataModelJsonStruct; visualisation: ImportShared.VisualisationState },
      {
        tableSchemas: ImportShared.TableSchemaJsonStruct[];
        type: ImportShared.DataSourceLocation;
        options?: { genAI?: boolean };
      }
    >({
      query: ({ tableSchemas, type, options }) => {
        const apiTableSchemas: ImportShared.ApiTableSchema[] = tableSchemas.map(
          (tableSchema): ImportShared.ApiTableSchema => ({
            name: tableSchema.name,
            fields: tableSchema.fields.map((field) => ({
              name: field.name,
              size: 0,
              // @TODO: This should be always here but in table schema it is optional
              // until we migrated type to rawType
              rawType: 'rawType' in field ? field.rawType : '',
              recommendedType: field.recommendedType,
              supportedTypes: 'supportedTypes' in field ? field.supportedTypes : undefined,
            })),
            primaryKeys: tableSchema.primaryKeys ?? [],
            foreignKeys: tableSchema.foreignKeys ?? [],
          }),
        );

        const body: ImportShared.CandidateGraphRequestBody = {
          schema: {
            type: type ?? '',
            tableSchemas: apiTableSchemas,
          },
          ai: options?.genAI === true,
        };

        return {
          url: '/v1/sources/candidate',
          method: 'POST',
          body: body,
        };
      },
      transformResponse: (candidateGraph: ImportShared.ApiCandidateGraphResponse) => {
        return parseApiCandidateGraphResponseToImportModel(candidateGraph);
      },
    }),
    fetchDataSourceDeclarations: builder.query<ImportShared.ApiDataSourceDeclarationResponse, void>({
      query: () => {
        return {
          url: '/v1/sources/declaration',
          method: 'GET',
        };
      },
      // We don't expect this to change often, set to 8 hours
      keepUnusedDataFor: 60 * 60 * 8,
      transformResponse: (data: ImportShared.ApiDataSourceDeclarations) => {
        if ('sources' in data) {
          const { sources } = data;
          if (!Array.isArray(sources)) {
            throw new Error('Failed to parse data source type definitions, "sources" is not an array');
          }
          // Return all valid sources and log invalid ones to make sure it doesn't break the app usability
          const validSources: ImportShared.DataSourceDefinition[] = [];
          const invalidSources: ImportShared.DataSourceDeclarationValidationError[] = [];
          sources.forEach((source) => {
            const { valid, errors: error } = ImportShared.validateDataSourceDefinition(source);
            if (valid) {
              validSources.push(source);
            } else {
              logger.error('Loaded invalid settings for data source', source, error);
              invalidSources.push({
                type: source.type,
                groupLabel: source.groupLabel,
                availability: source.availability,
                error: JSON.stringify(error),
              });
            }
          });
          // Change default field type from string to relevant type depending on the type
          validSources.forEach((source) => {
            source.sections.forEach((section) => {
              section.fields.forEach((field) => {
                if (ImportShared.isArrayFieldDefinition(field)) {
                  // @TODO: Implement, for now we just have string types in array field
                  return field;
                }
                if (field.default === undefined) {
                  return field;
                }
                let defaultField: ImportShared.FormFieldValue;
                if (field.type === 'integer') {
                  defaultField = !isNaN(Number(field.default)) ? Number(field.default) : 0;
                } else if (field.type === 'boolean') {
                  defaultField = Boolean(field.default);
                } else {
                  defaultField = field.default;
                }
                const newField: ImportShared.FieldDefinition = {
                  ...field,
                  default: defaultField,
                };
                return newField;
              });
            });
          });
          return { dataSources: validSources, validationErrors: invalidSources };
        }
        throw new Error(`Failed to parse data source type definitions, "sources" property is missing`);
      },
    }),
    createNewDataSource: builder.mutation<{ id: string }, ImportShared.DataSourceConfig>({
      query: (dataSource) => {
        return {
          url: '/v1/sources',
          method: 'POST',
          body: { ...buildDynamicDataSourceParamsPayloadByDataSourceConfig(dataSource), name: dataSource.name },
        };
      },
      invalidatesTags: [DATA_SOURCES_TAG],
    }),
    getDataSources: builder.query<
      ImportShared.SavedDataSourceConfig[],
      { dataSourceDeclarations: ImportShared.DataSourceDefinition[] | undefined }
    >({
      query: () => {
        return {
          url: '/v1/sources',
          method: 'GET',
        };
      },
      transformResponse: (data: ImportShared.ApiSavedDataSourceConfig[] | null, _api, { dataSourceDeclarations }) => {
        return (data ?? [])
          .map((apiSavedDataSourceConfig) => {
            return transformSavedDataSourceConfig(apiSavedDataSourceConfig, dataSourceDeclarations);
          })
          .filter((d) => !isNullish(d))
          .sort((a, b) => sortByTimeDesc(a.updatedAt, b.updatedAt));
      },
      providesTags: (result, _, __) => {
        return result
          ? [...result.map(({ id }) => ({ type: DATA_SOURCES_TAG, id })), DATA_SOURCES_TAG]
          : [DATA_SOURCES_TAG];
      },
    }),
    getDataSourceById: builder.query<
      ImportShared.SavedDataSourceConfig | undefined,
      { dataSourceId: string; dataSourceDeclarations: ImportShared.DataSourceDefinition[] | undefined }
    >({
      query: ({ dataSourceId }) => {
        return {
          url: `/v1/sources/${dataSourceId}`,
          method: 'GET',
        };
      },
      transformResponse: (data: ImportShared.ApiSavedDataSourceConfig, _api, { dataSourceDeclarations }) => {
        return transformSavedDataSourceConfig(data, dataSourceDeclarations);
      },
      providesTags: (result, _, __) => {
        return result ? [{ type: DATA_SOURCES_TAG, id: result.id }] : [];
      },
    }),
    deleteDataSourceById: builder.mutation<void, string>({
      query: (dataSourceId) => ({
        url: `/v1/sources/${dataSourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: DATA_SOURCES_TAG, id: arg }],
    }),
    updateDataSourceById: builder.mutation<
      void,
      { dataSourceId: string; dataSource: Partial<Omit<ImportShared.DataSourceConfig, 'type'>> }
    >({
      query: ({ dataSourceId, dataSource }) => {
        const payload: { name?: string; parameters?: Record<string, ImportShared.ApiDynamicField> } = {};
        if ('name' in dataSource) {
          payload.name = dataSource.name;
        }
        if ('fields' in dataSource && !isNullish(dataSource.fields)) {
          payload.parameters = buildDynamicParamsPayloadByDataSourceConfig(dataSource.fields);
        }
        return {
          url: `/v1/sources/${dataSourceId}`,
          method: 'PATCH',
          body: payload,
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: DATA_SOURCES_TAG, id: arg.dataSourceId }],
    }),
  }),
});

export const {
  useLazyFetchDataSourceSchemaWithRawDataSourceParametersQuery,
  useLazyFetchDataSourceSchemaWithDataSourceIdQuery,
  useLazyFetchCandidateGraphQuery,
  useFetchDataSourceDeclarationsQuery,
  useCreateNewDataSourceMutation,
  useGetDataSourcesQuery,
  useGetDataSourceByIdQuery,
  useDeleteDataSourceByIdMutation,
  useUpdateDataSourceByIdMutation,
} = sourceSchemaSlice;
